<template>
  <div class="funnel-stage-mapping">
    <div class="funnel-stage-mapping__intro">
      <strong>Left:</strong> funnel stage name and order. <strong>Right:</strong> conversation and stage(s) that trigger it &mdash; reaching <em>any one</em> is enough.
      <strong>Cumulative:</strong> reaching a later stage automatically counts the user in all earlier stages.
    </div>

    <div class="funnel-stage-mapping__header">
      <div>
        <div class="funnel-stage-mapping__title">Funnel Stages</div>
        <div class="funnel-stage-mapping__subtitle">Add stages in order. Each maps to conversation stage(s), from this journey or any conversation it embeds.</div>
      </div>
      <div class="funnel-stage-mapping__header-actions">
        <span>{{ rows.length }} stage{{ rows.length === 1 ? '' : 's' }} configured</span>
        <button type="button" class="btn btn-default btn-xs" @click="resetToDefaults">Reset to defaults</button>
      </div>
    </div>

    <div v-if="loadingConversations" class="funnel-stage-mapping__loading">
      <i class="fa fa-spinner fa-spin"></i> Loading this journey's conversations&hellip;
    </div>

    <div v-for="(row, index) in rows" :key="row._uid" class="funnel-stage-row">
      <div class="funnel-stage-row__number">{{ index + 1 }}</div>

      <div class="funnel-stage-row__name">
        <div class="funnel-stage-row__label">STAGE {{ index + 1 }}</div>
        <nv-select v-if="!row.isCustom" class="form-control" v-model="row.key" @change="onPresetSelected(row)">
          <option v-for="preset in funnelStagePresets" :key="preset.value" :value="preset.value">{{ preset.label }}</option>
        </nv-select>
        <input v-else type="text" class="form-control" v-model="row.label" placeholder="custom_stage_name" @input="emitChange">
        <a href="#" class="funnel-stage-row__toggle" @click.prevent="toggleCustom(row)">{{ row.isCustom ? 'Use preset' : 'Free text' }}</a>
        <div class="funnel-stage-row__row-actions">
          <button type="button" class="btn btn-default btn-xs" :disabled="index === 0" @click="moveRow(index, -1)" title="Move up"><i class="fa fa-arrow-up"></i></button>
          <button type="button" class="btn btn-default btn-xs" :disabled="index === rows.length - 1" @click="moveRow(index, 1)" title="Move down"><i class="fa fa-arrow-down"></i></button>
          <button type="button" class="btn btn-default btn-xs text-red" @click="removeRow(index)" title="Remove stage"><i class="fa fa-times"></i></button>
        </div>
      </div>

      <div class="funnel-stage-row__mapping">
        <div class="funnel-stage-row__mapping-label">Maps to conversation stage(s) &mdash; any one triggers this funnel stage</div>
        <div class="funnel-stage-row__chips">
          <span v-if="!row.conversationStages.length" class="text-muted">No stages mapped yet &mdash; select below</span>
          <span v-for="entry in row.conversationStages" :key="chipKey(entry)" class="funnel-stage-chip">
            <span class="funnel-stage-chip__convo">{{ entry.conversation }}</span> <i class="fa fa-angle-right"></i> {{ entry.stage }}
            <a href="#" @click.prevent="removeConversationStage(row, entry)">&times;</a>
          </span>
        </div>

        <div class="funnel-stage-row__add">
          <nv-select class="form-control" v-model="row._pendingConversation" @change="row._pendingStage = ''">
            <option value="">Choose conversation...</option>
            <option v-for="convo in conversations" :key="convo.name" :value="convo.name">{{ convo.name }}</option>
          </nv-select>
          <nv-select class="form-control" v-model="row._pendingStage" :disabled="!row._pendingConversation">
            <option value="">Choose stage...</option>
            <option v-for="stageKey in availableStagesFor(row)" :key="stageKey" :value="stageKey">{{ stageKey }}</option>
          </nv-select>
          <button type="button" class="btn btn-default" :disabled="!row._pendingStage" @click="addConversationStages(row)">
            + Add
          </button>
        </div>

        <div class="funnel-stage-row__options" v-if="row._pendingStage">
          <div class="funnel-stage-row__options-label">
            Answer options detected for this stage
          </div>
          <div v-if="pendingOptions(row).length" class="funnel-stage-row__chips">
            <span v-for="opt in pendingOptions(row)" :key="opt" class="funnel-stage-chip funnel-stage-chip--readonly">{{ opt }}</span>
          </div>
          <div v-else class="text-muted">No answer options detected for this stage.</div>
        </div>
      </div>
    </div>

    <button type="button" class="btn btn-default funnel-stage-mapping__add-stage" @click="addRow">+ Add funnel stage</button>
  </div>
</template>

<script>
import api from '../../../utils/api'
import { funnelStagePresets, defaultFunnelStageMapping } from './funnelStageConstants'

let uidCounter = 0
const nextUid = () => `funnel-stage-row-${++uidCounter}`

export default {
  name: 'funnel-stage-mapping',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    journeyName: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      funnelStagePresets,
      rows: this.buildInitialRows(),
      conversations: [],
      loadingConversations: false,
      suppressValueWatch: false
    }
  },
  watch: {
    value () {
      // Every local edit round-trips through emitChange() -> parent v-model -> this `value` prop.
      // Skip the rebuild for that echo, otherwise every keystroke recreates the row (and its
      // <input>), which drops focus after a single character.
      if (this.suppressValueWatch) {
        this.suppressValueWatch = false
        return
      }
      this.rows = this.buildInitialRows()
    },
    journeyName (name) {
      if (name) this.loadConversations()
    }
  },
  mounted () {
    if (this.journeyName) this.loadConversations()
  },
  methods: {
    buildInitialRows () {
      const source = (this.value && this.value.length) ? this.value : defaultFunnelStageMapping()
      return source.map(stage => ({
        ...stage,
        options: stage.options || [],
        conversationStages: (stage.conversationStages || []).map(entry => (typeof entry === 'string' ? { conversation: this.journeyName, stage: entry } : entry)),
        _pendingConversation: '',
        _pendingStage: '',
        _loadingOptions: false,
        _uid: nextUid()
      }))
    },
    loadConversations () {
      this.loadingConversations = true
      api.buildRequest('getJourneyConversationsList', 'GET', { app: this.journeyName })
        .then(response => {
          this.loadingConversations = false
          if (response.status === 200) {
            this.conversations = response.data.payload || []
          }
        })
        .catch(() => {
          this.loadingConversations = false
        })
    },
    stagesFor (conversationName) {
      if (!conversationName) return []
      const convo = this.conversations.find(c => c.name === conversationName)
      return convo ? convo.stages : []
    },
    availableStagesFor (row) {
      const added = row.conversationStages.filter(entry => entry.conversation === row._pendingConversation).map(entry => entry.stage)
      return this.stagesFor(row._pendingConversation).filter(stageKey => !added.includes(stageKey))
    },
    chipKey (entry) {
      return `${entry.conversation}::${entry.stage}`
    },
    // Answer options come straight from the conversation config (returned with the conversations
    // list) - no API call. Options are the ones authored on that conversation stage.
    optionsFor (conversationName, stageKey) {
      if (!conversationName || !stageKey) return []
      const convo = this.conversations.find(c => c.name === conversationName)
      return (convo && convo.stageOptions && convo.stageOptions[stageKey]) || []
    },
    // Preview of the stage currently picked in the dropdown, shown only while selecting.
    pendingOptions (row) {
      return this.optionsFor(row._pendingConversation, row._pendingStage)
    },
    // Aggregate options of all mapped stages - persisted into row.options for downstream use
    // (e.g. the Audience Builder's Readiness filter).
    recomputeOptions (row) {
      row.options = [...new Set(row.conversationStages.flatMap(entry => this.optionsFor(entry.conversation, entry.stage)))]
    },
    addConversationStages (row) {
      if (!row._pendingConversation || !row._pendingStage) return
      const alreadyAdded = row.conversationStages.some(entry => entry.conversation === row._pendingConversation && entry.stage === row._pendingStage)
      if (!alreadyAdded) {
        row.conversationStages.push({ conversation: row._pendingConversation, stage: row._pendingStage })
      }
      // Only the picked stage clears - the conversation stays selected so more stages can be added right after.
      row._pendingStage = ''
      this.recomputeOptions(row)
      this.emitChange()
    },
    removeConversationStage (row, entry) {
      row.conversationStages = row.conversationStages.filter(e => !(e.conversation === entry.conversation && e.stage === entry.stage))
      this.recomputeOptions(row)
      this.emitChange()
    },
    onPresetSelected (row) {
      const preset = funnelStagePresets.find(p => p.value === row.key)
      row.label = preset ? preset.label : row.key
      this.emitChange()
    },
    toggleCustom (row) {
      row.isCustom = !row.isCustom
      if (row.isCustom) {
        row.key = `custom_${nextUid()}`
        row.label = ''
      } else {
        const preset = funnelStagePresets[0]
        row.key = preset.value
        row.label = preset.label
      }
      this.emitChange()
    },
    addRow () {
      this.rows.push({ key: `custom_${nextUid()}`, label: '', isCustom: true, conversationStages: [], options: [], _pendingConversation: '', _pendingStage: '', _loadingOptions: false, _uid: nextUid() })
      this.emitChange()
    },
    removeRow (index) {
      this.rows.splice(index, 1)
      this.emitChange()
    },
    moveRow (index, direction) {
      const target = index + direction
      if (target < 0 || target >= this.rows.length) return
      const rows = [...this.rows]
      const [moved] = rows.splice(index, 1)
      rows.splice(target, 0, moved)
      this.rows = rows
      this.emitChange()
    },
    async resetToDefaults () {
      const result = await this.$swal({
        title: 'Reset to default funnel stages?',
        text: `This replaces the current list with the ${funnelStagePresets.length} preset stages, unmapped.`,
        showCancelButton: true,
        confirmButtonText: 'Reset',
        cancelButtonText: 'Cancel'
      })
      if (result.dismiss) return
      this.rows = defaultFunnelStageMapping().map(stage => ({ ...stage, conversationStages: [], _pendingConversation: '', _pendingStage: '', _loadingOptions: false, _uid: nextUid() }))
      this.emitChange()
    },
    emitChange () {
      const sanitized = this.rows.map(({ _pendingConversation, _pendingStage, _loadingOptions, _uid, ...rest }) => rest)
      this.suppressValueWatch = true
      this.$emit('input', sanitized)
    }
  }
}
</script>

<style scoped>
.funnel-stage-mapping__intro {
  background: #E6F5F4;
  border: 1px solid #B9E3DF;
  border-radius: 4px;
  padding: 10px 14px;
  margin-bottom: 15px;
  font-size: 13px;
  color: #1E3A5F;
}
.funnel-stage-mapping__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}
.funnel-stage-mapping__title {
  font-weight: 600;
}
.funnel-stage-mapping__subtitle {
  font-size: 12px;
  color: #718096;
}
.funnel-stage-mapping__header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #718096;
  white-space: nowrap;
}
.funnel-stage-mapping__loading {
  color: #718096;
  font-size: 13px;
  margin-bottom: 10px;
}
.funnel-stage-row {
  display: flex;
  gap: 15px;
  align-items: flex-start;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 12px;
}
.funnel-stage-row__number {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0D9488;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 12px;
  flex-shrink: 0;
}
.funnel-stage-row__name {
  width: 220px;
  flex-shrink: 0;
}
.funnel-stage-row__label {
  font-size: 11px;
  font-weight: bold;
  color: #a0aec0;
  margin-bottom: 4px;
}
.funnel-stage-row__toggle {
  display: inline-block;
  font-size: 12px;
  margin-top: 4px;
}
.funnel-stage-row__row-actions {
  margin-top: 6px;
}
.funnel-stage-row__mapping {
  flex: 1;
  min-width: 0;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  padding: 10px;
}
.funnel-stage-row__mapping-label {
  font-size: 12px;
  color: #718096;
  margin-bottom: 8px;
}
.funnel-stage-row__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
  min-height: 24px;
}
.funnel-stage-chip {
  background: #E6F5F4;
  border: 1px solid #7DD3C8;
  border-radius: 3px;
  padding: 3px 8px;
  font-size: 12px;
  white-space: nowrap;
}
.funnel-stage-chip__convo {
  color: #1E3A5F;
  font-weight: 600;
}
.funnel-stage-chip a {
  margin-left: 6px;
  color: #1E3A5F;
}
.funnel-stage-row__options {
  margin-bottom: 10px;
  padding: 8px 10px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}
.funnel-stage-row__options-label {
  font-size: 11px;
  color: #718096;
  margin-bottom: 6px;
}
.funnel-stage-chip--readonly {
  background: #edf2f7;
  border-color: #cbd5e0;
}
.funnel-stage-row__add {
  display: flex;
  gap: 8px;
}
.funnel-stage-row__add .nv-select {
  flex: 1;
  min-width: 0;
}
.funnel-stage-mapping__add-stage {
  width: 100%;
}
.text-red {
  color: #e53e3e;
}
</style>



// WEBPACK FOOTER //
// FunnelStageMapping.vue?1030b504